import { useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetPolicySummaryQueryKey,
  glacierPolicyApiListUnderwriterNotesQueryKey,
  glacierPolicyApiReferPolicyMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { formatErrorMessage } from '../../utils';

type ConfirmForm = {
  confirm: boolean;
  note: string;
};

export const Refer = ({ accountId }: { accountId: string }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();
  const permissionsQk = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: { account_id: accountId },
  });
  const summariesQk = glacierPolicyApiGetPolicySummaryQueryKey();
  const policyQk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });
  const uwNotesQk = glacierPolicyApiListUnderwriterNotesQueryKey({
    path: { account_id: accountId },
  });

  const refer = useMutation({
    ...glacierPolicyApiReferPolicyMutation(),
    onSuccess() {
      qc.refetchQueries({ queryKey: permissionsQk });
      qc.refetchQueries({ queryKey: summariesQk });
      qc.refetchQueries({ queryKey: policyQk });
      qc.refetchQueries({ queryKey: uwNotesQk });
      closeRef.current?.click();
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Referred</code>
          </pre>
        ),
      });
    },
    onError(error) {
      toast({
        title: 'Error',
        description: formatErrorMessage(error),
      });
    },
  });

  const form = useForm<ConfirmForm>({
    defaultValues: {
      confirm: false,
      note: '',
    },
  });

  const { handleSubmit, watch, register, setValue } = form;
  const watchConfirm = watch('confirm');

  const onSubmit: SubmitHandler<ConfirmForm> = (data) => {
    refer.mutate({
      body: { note: data.note.trim() || undefined },
      path: {
        account_id: accountId,
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>Refer Policy</DialogTitle>
          <DialogDescription>
            This policy will be sent to the assigned underwriter for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="refer-note">Note for underwriter (optional)</Label>
            <Textarea
              id="refer-note"
              rows={5}
              placeholder="Add context for the underwriter..."
              {...register('note')}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="refer-confirm"
              checked={watchConfirm}
              onCheckedChange={(checked) =>
                setValue('confirm', checked === true)
              }
              className="mt-0.5"
            />
            <Label
              htmlFor="refer-confirm"
              className="cursor-pointer text-sm font-medium leading-relaxed"
            >
              Confirm to refer this policy to the assigned underwriter for
              review
            </Label>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            className="bg-brand-green text-brand-0 transition hover:bg-brand-green hover:opacity-70 hover:duration-300"
            disabled={!watchConfirm || refer.isPending}
          >
            {refer.isPending ? 'Submitting...' : 'Submit'}
          </Button>
          <DialogClose ref={closeRef} className="hidden" />
        </DialogFooter>
      </form>
    </Form>
  );
};
